import React, {FunctionComponent, useEffect, useMemo, useReducer} from 'react';
import {useDispatch} from "react-redux";

import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {ContentCard, ContentToolbar, NoIntegration, NoIntegrationCallout} from "../components";
import {useCreateBlock, useExecuteRequest, useGetIntegrations, useUpdateBlockContentJson} from "../hooks";
import {initialTableauEmbedContentState, tableauEmbedContentReducer} from "./store";
import {createDefaultContent, removeTrustedTicketFromUrl} from "./utils";
import {
  setInit,
  setTableauEmbedContentForm,
  setTableauEmbedContentFormKeysValues,
  setTableauEmbedContentFormKeyValue
} from "./store/actions";
import {loadWorkbooks, unsetWorkbooks} from '../../../store/actions/tableauActions';
import {IItem} from "../../select";
import {integrationToSelectOption} from "../utils";
import TableauEmbedForm from "./TableauEmbedForm";
import {useCurrentUser} from "../../../hooks";
import {isAdmin} from "../../../utils/auth";

interface TableauEmbedProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const TableauEmbed: FunctionComponent<TableauEmbedProps> = ({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: TableauEmbedProps) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] = useReducer(tableauEmbedContentReducer, initialTableauEmbedContentState);
  const uid = content.uid;
  const integrationId = content.integration_id;

  // as we don't have a separate type for tableau embed, we need to convert it to tableau
  const {integrations, hasIntegrations} = useGetIntegrations('tableau');
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);

  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  useCreateBlock(content, createDefaultContent());

  useEffect(() => {
    /*
     * Initial load on edit
     */
    if (uid) {
      const cleanedContent = {
        ...content,
        embed_url: removeTrustedTicketFromUrl(content.embed_url || ""),
      };
      contentDispatch(setTableauEmbedContentForm(cleanedContent));
    } else if (!uid && !!content.from_block_id) {
      /*
       * Comes from saved block
       */
      const cleanedContent = {
        ...content,
        embed_url: removeTrustedTicketFromUrl(content.embed_url || ""),
      };
      contentDispatch(setTableauEmbedContentForm(cleanedContent));
    } else {
      contentDispatch(setInit(true));
    }

    return () => {
      dispatch(unsetWorkbooks());
    }
  }, []);

  const onExecute = () => {
    if (!contentState.form.integration_id) {
      return;
    }

    const block = {
      content_json: {
        ...contentState.form,
      }
    };
    executeRequest(block);
  };

  const onKeyValueChange = (key: string) => (value: any) => {
    updateBlockContentJson(key, value);
    contentDispatch(setTableauEmbedContentFormKeyValue(key, value));
  };

  const onKeysValuesChange = (keys: string[], values: any[]) => {
    /*
     * TODO: change this from [...keys], [...values] to {key: value}
     */
    updateBlockContentJson(keys, values);
    contentDispatch(setTableauEmbedContentFormKeysValues(keys, values));
  };

  const onIntegrationIdChange = (integrationId: string) => {
    /*
     * Update block on the backend;
     * Update block in useReducer;
     * Load workbooks and custom views for the integration;
     */
    dispatch(loadWorkbooks(uid as string, integrationId, undefined, true));

    const newContent = {
      'integration_id': integrationId,
      'workbook_id': '',
      'workbook_name': '',
      'view_id': '',
      'view_name': '',
      'embed_url': '',
      'thumbnail': '',
    };
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onWorkbookChange = (workbook: IItem) => {
    /*
     * Reset view_id and custom_view_id.
     */
    const newContent = {
      'workbook_id': workbook.value,
      'workbook_name': workbook.label,
      'view_id': '',
      'view_name': '',
    }
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onViewChange = (view: IItem) => {
    /*
     * Reset custom_view_id.
     */
    const newContent = {
      'view_id': view.value,
      'view_name': view.label,
    }
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onEmbedUrlChange = (embedUrl: string) => {
    const cleanedEmbedUrl = removeTrustedTicketFromUrl(embedUrl);
    updateBlockContentJson('embed_url', cleanedEmbedUrl);
    contentDispatch(setTableauEmbedContentFormKeyValue('embed_url', cleanedEmbedUrl));
  };

  const onThumbnailChange = (thumbnail: string) => {
    updateBlockContentJson('thumbnail', thumbnail);
    contentDispatch(setTableauEmbedContentFormKeyValue('thumbnail', thumbnail));
  }

  return (
    <>
      {isAdmin(currentUser) ? (
        <ContentCard
          key={content.id}
          type={content.type}
          contentId={contentState.init ? content.uid : null}
          disabled={!integrationId || !hasIntegrations}
          noContentElement={
            integrationId || hasIntegrations ? null : (
              <NoIntegration
                contentId={uid as string}
                type={content.type}
                onDelete={actions.onDelete}
                showDelete={options.showDelete}
              />
            )
          }
          allowCollapse={options.allowCollapse}
          showHeader={options.showHeader}
          showFooter={options.showFooter}
        >
          <div className="content__tableau">
            <ContentToolbar
              contentId={uid as string}
              executing={executing}
              onExecute={options.showRun ? onExecute : null}
              selected={contentState.form.integration_id}
              onSelect={onIntegrationIdChange}
              options={integrations.map(integrationToSelectOption)}
              updating={updating}
              selectLabel="Select Integration"
              onDelete={actions.onDelete}
              showDelete={options.showDelete}
              showSave={options.showSave}
              disabled={options.readOnly}
            />
            <div className="content-card__content__form">
              {!contentState.form.integration_id ? (
                <NoIntegrationCallout/>
              ) : (
                <TableauEmbedForm
                  uid={uid as string}
                  integrationId={contentState.form.integration_id}
                  workbookId={contentState.form.workbook_id}
                  workbookName={contentState.form.workbook_name || ''}
                  viewId={contentState.form.view_id || ''}
                  viewName={contentState.form.view_name || ''}
                  filters={contentState.form.filters || ''}
                  embedUrl={contentState.form.embed_url || ''}
                  thumbnail={contentState.form.thumbnail || ''}
                  onWorkbookChange={onWorkbookChange}
                  onViewChange={onViewChange}
                  onFiltersChange={onKeyValueChange('filters')}
                  onEmbedUrlChange={onEmbedUrlChange}
                  onThumbnailChange={onThumbnailChange}
                  disabled={options.readOnly}
                />
              )}
            </div>
          </div>
        </ContentCard>
      ) : (
        <div className="content-card__content__form">
          {!contentState.form.integration_id ? (
            <NoIntegrationCallout/>
          ) : (
            <TableauEmbedForm
              uid={uid as string}
              integrationId={contentState.form.integration_id}
              workbookId={contentState.form.workbook_id}
              workbookName={contentState.form.workbook_name || ''}
              viewId={contentState.form.view_id || ''}
              viewName={contentState.form.view_name || ''}
              filters={contentState.form.filters || ''}
              embedUrl={contentState.form.embed_url || ''}
              thumbnail={contentState.form.thumbnail || ''}
              onWorkbookChange={onWorkbookChange}
              onViewChange={onViewChange}
              onFiltersChange={onKeyValueChange('filters')}
              onEmbedUrlChange={onEmbedUrlChange}
              onThumbnailChange={onThumbnailChange}
              disabled={options.readOnly}
            />
          )}
        </div>
      )}
    </>
  );
};
